// Anzeige-Bausteine fuer das Abwaegen und das Gespraechs-Panel.
//
// Stand bis zum 23.09.2026 in VerhandlungsPassus.jsx. Seit das Abwaegen ein
// eigener Bereich unter "Vertraege" ist, brauchen zwei Seiten dieselben
// Bausteine - deshalb stehen sie hier und nicht doppelt.

import React from 'react';
import { ChevronDown } from 'lucide-react';
import { T, ueberzeileStil, markeStil } from '../../lib/designTokens';
import { ohneMarkdown } from '../../lib/ohneMarkdown';
import { belegArt, istAusDemVertrag, urteilsTon } from '../../lib/abwaegen';

/** Die Abschnittsmarke aus designTokens, optional mit Nummer davor. */
export function Ueberzeile({ nummer, children, stil = null }) {
  return (
    <div style={{ ...ueberzeileStil, ...(stil || {}) }}>
      {nummer ? `${nummer} · ` : ''}{children}
    </div>
  );
}

// Die folgenden Bausteine standen bis zum 23.09.2026 nur im Abwaegen. Seit
// das Gespraechs-Panel im selben Stil gebaut ist, teilen sich beide sie.

/** Die Nummer links in der Kopfzeile einer Karte. */
export function NummerKreis({ children }) {
  return (
    <span style={{
      flexShrink: 0, minWidth: 22, height: 22, borderRadius: 999, background: T.grund,
      border: `1px solid ${T.randWeich}`, color: T.muted, fontSize: T.tMikro, fontWeight: 700,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px',
    }}>{children}</span>
  );
}

/** Ruhiger Schalter fuer eingeklappten Hintergrund (Begruendung, Belege …). */
export function Aufklapper({ offen, onClick, children, farbe = T.muted, titel = '' }) {
  return (
    <button type="button" onClick={onClick} title={titel} aria-expanded={offen} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent',
      padding: '2px 0', fontSize: T.tMikro, fontWeight: 700, color: offen ? T.navy : farbe, cursor: 'pointer',
    }}>
      {children}
      <ChevronDown size={12} style={{ transform: offen ? 'rotate(180deg)' : 'none', transition: 'transform 120ms' }} />
    </button>
  );
}

/** Knopf ohne Rahmen - fuer Nebenwege wie "Zurueck zu offen". */
export const textKnopfStil = {
  display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent',
  padding: '2px 0', fontSize: T.tMikro, fontWeight: 700, color: T.muted, cursor: 'pointer',
};

/**
 * Solange nichts da ist: die Schritte des Bereichs, der erste hervorgehoben.
 * schritte: [[titel, zeile], ...]
 */
export function Wegweiser({ schritte }) {
  return (
    <div style={{ maxWidth: 420, margin: '28px auto 0', display: 'flex', flexDirection: 'column', gap: 18 }}>
      {schritte.map(([titel, zeile], i) => (
        <div key={titel} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', opacity: i === 0 ? 1 : 0.55 }}>
          <span style={{
            flexShrink: 0, width: 26, height: 26, borderRadius: 999, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', fontSize: T.tSm, fontWeight: 700,
            background: i === 0 ? T.orange : T.flaeche, color: i === 0 ? '#FFFFFF' : T.muted,
            border: `1px solid ${i === 0 ? T.orange : T.rand}`,
          }}>{i + 1}</span>
          <div style={{ paddingTop: 3 }}>
            <div style={{ fontSize: T.tTitel, fontWeight: 700, color: T.navy }}>{titel}</div>
            <div style={{ fontSize: T.tMeta, color: T.muted, marginTop: 2 }}>{zeile}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Im Navi sagt die Farbe der Marke, wie dringend etwas ist - nicht, zu welcher
// Rubrik es gehoert. Hier genauso: Die Farbe kommt aus dem Urteil, nicht aus
// der Spalte. Vorher war "wirtschaftlich" immer tuerkis und "verkaeuflich"
// immer orange, auch wenn beide "unproblematisch" sagten - die Farbe log.
//
// Welches Urteil welchen Ton hat (unproblematisch -> gut, teuer/kritisch ->
// gefahr ...), steht seit dem 23.09.2026 in src/lib/abwaegen.js (urteilsTon) -
// die mobile Oberflaeche braucht dieselbe Zuordnung.
const TON_FARBE = {
  gut: { schrift: T.gut, rand: T.gutRand, grund: T.gutGrund },
  info: { schrift: T.info, rand: T.infoRand, grund: T.infoGrund },
  gefahr: { schrift: T.gefahr, rand: T.gefahrRand, grund: T.gefahrGrund },
  neutral: { schrift: T.muted, rand: T.rand, grund: T.grund },
};

export function urteilsFarbe(bewertung) {
  return TON_FARBE[urteilsTon(bewertung)];
}

// Warum Herkunft und Belastbarkeit mit angezeigt werden, statt still zu
// filtern: Die Wissensbasis fuehrt Vertragstext und Schulungsmaterial
// nebeneinander und kennzeichnet beides. Ihre eigene Regel lautet "Der
// Vertrag gilt, das Schulungsmaterial ist nachrangig". Wer das verbirgt,
// nimmt dem Bearbeiter genau die Angabe, die er zum Einordnen braucht.
// Die Texte dazu stehen in src/lib/abwaegen.js (belegArt).

/** Belege aus dem Wissensdienst zu einem abgewogenen Punkt. */
export function Belegliste({ eintrag, laedt = false }) {
  if (!eintrag) {
    return laedt ? (
      <div style={{ marginTop: 8, fontSize: T.tMikro, color: T.faint }}>Wissensbasis wird befragt …</div>
    ) : null;
  }

  if (!eintrag.treffer?.length) {
    return (
      <div style={{ marginTop: 8, fontSize: T.tMikro, color: T.faint }}>
        Wissensbasis: {eintrag.grund || 'nichts gefunden'}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10, paddingTop: 9, borderTop: `1px solid ${T.hairline}` }}>
      <Ueberzeile stil={{ marginBottom: 7 }}>Aus der Wissensbasis</Ueberzeile>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {eintrag.treffer.map((t, i) => {
          // Belastbarkeit 1 ist der Vertrag selbst - der wiegt schwerer als
          // alles andere und wird deshalb gruen markiert, nicht grau.
          const ausDemVertrag = istAusDemVertrag(t);
          return (
            <div key={`${t.slug || t.titel}-${i}`} style={{
              border: `1px solid ${ausDemVertrag ? T.gutRand : T.randWeich}`,
              background: ausDemVertrag ? T.gutGrund : T.grund,
              borderRadius: T.rChip, padding: '8px 10px',
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                {/* Der Titel behaelt Mindestbreite: Pohls Fundstellen sind lang
                    ("abschnitt 2.5 Betreten — wer darf …") und quetschten ihn
                    sonst in eine Spalte von einem Wort Breite. */}
                <Ueberzeile stil={{ flex: '1 1 220px', minWidth: 0, color: ausDemVertrag ? T.gut : T.label }}>
                  {ohneMarkdown(t.titel) || 'Ohne Titel'}
                </Ueberzeile>
                {t.fundstelle ? (
                  <span style={{ flex: '0 1 auto', minWidth: 0, fontSize: T.tMikro, fontWeight: 700, color: T.body }}>{ohneMarkdown(t.fundstelle)}</span>
                ) : null}
                <span style={markeStil(ausDemVertrag ? T.gut : T.muted, ausDemVertrag ? T.gutRand : T.rand, T.flaeche)}>
                  {belegArt(t)}
                </span>
              </div>

              {t.inhalt ? (
                <div style={{ fontSize: T.tMeta, lineHeight: 1.55, color: T.ink }}>{ohneMarkdown(t.inhalt)}</div>
              ) : null}

              {/* Eine Widerlegung ist hier keine Verkaufshilfe, sondern eine
                  Richtigstellung: oben die Behauptung, darunter was stimmt,
                  dazu die Fundstelle. Beides gehoert zusammen. */}
              {t.widerlegungen?.map((w, j) => (
                <div key={j} style={{
                  marginTop: 7, padding: '7px 9px', borderRadius: T.rChip,
                  background: T.flaeche, border: `1px solid ${T.warnRand}`,
                }}>
                  <Ueberzeile stil={{ color: T.warn, marginBottom: 4 }}>
                    Häufiger Irrtum{w.fundstelle ? ` · ${w.fundstelle}` : ''}
                  </Ueberzeile>
                  {w.aussage ? (
                    <div style={{ fontSize: T.tMikro, color: T.muted, lineHeight: 1.5, textDecoration: 'line-through', marginBottom: 3 }}>
                      {ohneMarkdown(w.aussage)}
                    </div>
                  ) : null}
                  {w.richtig ? (
                    <div style={{ fontSize: T.tMeta, color: T.ink, lineHeight: 1.55 }}>{ohneMarkdown(w.richtig)}</div>
                  ) : null}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
